import {
  foldSessionSetsIntoPRs,
  isSetPR,
  type PRsByExercise,
  type WorkoutSet,
} from '@heraion/shared';

import type { DraftEntry, DraftSet } from './state/loggerDraftTypes';
import {
  buildWorkoutSetFromDraft,
  completedDraftSetsFor,
} from './workoutMapping';

// Single source of truth for "would this set flash a PR right now",
// shared by the click-time pill cycle and the records-arrive-late
// safety-net effect in LoggerScreen (Track R — was duplicated inline in
// both). Builds the same WorkoutSet shape the server validates against
// (toCanonicalKg, locale parse, weighted vs bodyweight) so the PR check
// sees exactly what the save will. When prHistory hasn't hydrated yet
// the set is unbuildable-for-PR-purposes: built is null and isPr false,
// so the caller shows no PR cue rather than blocking the tap.
export function detectSetPr(args: {
  set: DraftSet;
  exerciseId: string;
  isBodyweight: boolean;
  prHistory: PRsByExercise | null;
  // Sets of the SAME exercise already completed EARLIER in this session
  // (built WorkoutSet shape). Folded into the comparison history so a
  // 2nd identical set isn't re-flagged as a PR — the 1st already holds
  // the bucket. Omit/empty for a true first-of-session check.
  priorSessionSets?: readonly WorkoutSet[];
}): { built: WorkoutSet | null; isPr: boolean } {
  const { set, exerciseId, isBodyweight, prHistory, priorSessionSets } = args;
  const built = prHistory ? buildWorkoutSetFromDraft(set, isBodyweight) : null;
  const history =
    prHistory && priorSessionSets && priorSessionSets.length > 0
      ? foldSessionSetsIntoPRs(prHistory, exerciseId, priorSessionSets)
      : prHistory;
  const isPr =
    history !== null &&
    built !== null &&
    isSetPR({ set: built, exerciseId, history });
  return { built, isPr };
}

// Collect the PR-eligible sets of `exerciseId` already completed in the
// current session BEFORE the candidate, built into the canonical
// WorkoutSet shape isSetPR compares against. "Before" = `completedAt`
// strictly earlier than `candidateCompletedAt`; pass `null` for the live
// pending→done tap (the candidate isn't completed yet, so every
// already-completed set of the exercise counts and the candidate itself
// — still pending — is naturally excluded). PR scope is per exerciseId,
// so we scan ALL entries (supersets / the same lift logged twice),
// matching prsFromHistory's whole-workout aggregation. The strict `<`
// keeps the earliest of two identical sets as the PR holder.
export function priorSessionSetsForPr(
  entries: readonly DraftEntry[],
  exerciseId: string,
  isBodyweight: boolean,
  candidateCompletedAt: string | null,
): WorkoutSet[] {
  const out: WorkoutSet[] = [];
  for (const { set: s } of completedDraftSetsFor(entries, exerciseId)) {
    // A fail is valid PR history (AP-0p model): its `reps` are the clean
    // reps done and `fail` is a pure label — a 3×165 fail can be a PR.
    // `completedAt` is non-null by construction — completedDraftSetsFor
    // yields only completed sets — but the DraftSet type still allows null.
    const completedAt = s.completedAt ?? '';
    if (candidateCompletedAt !== null && !(completedAt < candidateCompletedAt)) {
      continue;
    }
    const built = buildWorkoutSetFromDraft(s, isBodyweight);
    if (built) out.push(built);
  }
  return out;
}

// Records-arrive-late safety net (1.99.x — inbox 74017a4d). When records
// arrive AFTER the user has already tapped a pending→done pill (slow
// network, cold cache, repeat-into-Logger race) the click-time PR check
// can't fire. Walk every already-done set and stamp prFlash retroactively
// on any that would have flashed. Skips already-PR sets and pending sets. A
// FAILED set IS eligible (AP-0p model: reps = clean count, fail = pure label,
// so a fail can be a PR) — this is also what re-stamps the badge on a fail
// after the live flash was cleared at fail time. Uses the same detectSetPr
// path as the live tap so the result matches. Returns a NEW entries array
// (clone-on-write) when at least one set changed, or null when nothing needed
// stamping.
export function computeRetroactivePrStamps(
  entries: DraftEntry[],
  prHistory: PRsByExercise,
): DraftEntry[] | null {
  let nextEntries = entries;
  let changed = false;
  entries.forEach((entry, entryIdx) => {
    entry.sets.forEach((set, setIdx) => {
      if (set.completedAt === null) return;
      if (set.prFlash) return;
      const { built, isPr } = detectSetPr({
        set,
        exerciseId: entry.exerciseId,
        isBodyweight: entry.isBodyweight,
        prHistory,
        priorSessionSets: priorSessionSetsForPr(
          entries,
          entry.exerciseId,
          entry.isBodyweight,
          set.completedAt,
        ),
      });
      if (!built || !isPr) return;
      if (!changed) {
        nextEntries = nextEntries.slice();
        changed = true;
      }
      const ent = nextEntries[entryIdx]!;
      const sets = ent.sets.slice();
      sets[setIdx] = { ...set, prFlash: true };
      nextEntries[entryIdx] = { ...ent, sets };
    });
  });
  return changed ? nextEntries : null;
}
